/**
 * Диалог «Запустить генерацию».
 *
 * КНОПКА НЕ ЗОВЁТ СЕРВИС ГЕНЕРАЦИИ И НЕ МОЖЕТ ЕГО ПОЗВАТЬ: у того нет HTTP-сервера, входящий
 * доступ к нему запрещён таблицей топологии, а наружу открыт только api.anthropic.com
 * (`domain/generation-requests.ts`). Кнопка кладёт заявку в очередь — но сервис теперь демон
 * и опрашивает очередь раз в ~1500 мс, поэтому прогон начинается через секунды, а не
 * «следующим прогоном» по расписанию.
 *
 * ЧТО ИНТЕРФЕЙС ОБЕЩАЕТ, И РОВНО СТОЛЬКО. Не «пост сейчас»: прогон идёт десятки секунд, и
 * готовый черновик — его конец, а не начало. Обещание здесь такое: работа начнётся сразу, а
 * ход её будет виден в шапке (`RunStatusBar` слева от кнопки) — по этапам, с исходом в
 * конце. Формулировка «заберёт следующим прогоном» была верна, пока сервис запускался по
 * расписанию, и врала бы теперь в другую сторону — занижая.
 *
 * ОБЕЩАНИЕ ВЕТВИТСЯ ПО РЕЖИМУ (правка 6 от 2026-09-08). «Сначала мониторинг обойдёт
 * источники (1–3 минуты)» — правда для обычного прогона и ЛОЖЬ для прогона по контексту:
 * `createMonitoringRequest` для него не вызывается вовсе (§6.1), тема берётся из самого
 * контекста, и писатель начинает писать в те же секунды. Одна формулировка на два пути
 * врала бы ровно в том месте, ради которого правку и просили.
 *
 * ФОРМАТЫ — БРЕНДА (с 2026-11-02; до того два зашитых, релиз 2026-10-14). Чипы — список
 * `selectable_formats` бренда из `GET /api/v1/brands` (`brandFormats`): GeoBoost — «Новость» и
 * «Дайджест недели», AGI Boardroom — «Новость» и «Обзор конференции». Поля нет — прежние два.
 * «Новость» — умолчание и нажата при открытии. «Любой», «другой код…» и поле кода сняты, и
 * `known_formats` (история бренда) чипы не читают: она помнит снятые коды и возвращала бы их в
 * выбор вечно. Подписи — `components/formatLabel.ts` (`formatLabel`). Нажатый формат — выбор
 * человека поверх списка, а не копия: список, приехавший после открытия, не оставит нажатым код,
 * которого у бренда нет.
 *
 * ДАЙДЖЕСТ НЕ БЕРЁТ НИ ССЫЛКУ, НИ КОНТЕКСТ. Его собирает мониторинг из новостей за последние
 * пять дней; заявку «дайджест + ссылка» монитор отклоняет (`run.ts`, «не собирается по одной
 * ссылке»), а прогон по контексту обходит мониторинг вовсе — писатель получил бы одну тему
 * без трёх–пяти материалов. Поэтому при выбранном дайджесте поле ссылки заменено подсказкой,
 * чип «Задать контекст» не нажимается, и набранное в них на сервер не уходит.
 *
 * ОБЗОР КОНФЕРЕНЦИИ БЕРЁТ КОНТЕКСТ, НО НЕ ССЫЛКУ (build spec 2026-11-02 §5). Материалы ищет
 * мониторинг в источниках списка наблюдения бренда за последние 7 дней и сам проверяет, что
 * конференция прошла; заявку с `source_url` бэкенд отбивает. Поэтому ссылка заменена строкой, а
 * вместо подсказки — необязательное поле «Конференция»: оно уходит `prompt`, и мониторинг берёт
 * из списка только конференции, чьё название или ключевые слова с ним совпали. Своё поле, а не
 * подсказка новости: подсказка,
 * набранная для новости, ушла бы фильтром конференции и закрыла бы заявку «не нашлось».
 * «Задать контекст» работает: вставленные анонсы — прогон по контексту без обхода, и ссылки в
 * посте тогда нет. Второй обзор той же конференции в этом году бэкенд отбивает 409 — его текст
 * диалог показывает как есть.
 *
 * СТИЛЬ ЭТОГО ПРОГОНА И СРАВНЕНИЕ ДВУХ СТИЛЕЙ (docs/release-2026-09-19.md §5.4). Ряд «Стиль» —
 * выбор на ОДНО нажатие, а не настройка бренда: умолчание живёт в «Настройках» шапки и здесь
 * только отмечено «(по умолчанию)». Нажатие на умолчание шлёт `null`, как «(дефолт)» у модели:
 * заявка хранит выбор человека, а «по умолчанию» значит «тем, что стоит в момент прогона».
 * «Сравнить с…» раскрывает второй ряд — второй стиль той же заявки; первый в нём не
 * предлагается вовсе, потому что сравнение стиля с самим собой бэкенд отвергает
 * (`style_compare_same`), а оплатить пост дважды ради одинакового текста незачем.
 *
 * НИКАКОГО МЕЛКОГО ТЕКСТА РАДИ ОБЪЯСНЕНИЙ (§1 партии 19.09). Что значит режим контекста,
 * дайджест недели и сравнение стилей — в поповерах ⓘ рядом с этими действиями; в теле
 * диалога только поля, чипы и проверки.
 */

import { isHostAllowed, linkHostOf } from '@smm/contracts';
import {
  GENERATION_CONTEXT_MAX,
  GENERATION_PROMPT_MAX,
  GENERATION_SOURCE_URL_RE,
} from '@smm/contracts/schemas';
import { type JSX, useId, useState } from 'react';
import { Link } from 'wouter';
import { ApiRequestError, post } from '../api/client.ts';
import type {
  GenerationRequest,
  GenerationRequestState,
  GenerationRequestsResponse,
} from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { brandFormats, useBrands } from '../brand.tsx';
import { shortMsk } from './format.ts';
import {
  EVENT_REVIEW_FORMAT,
  formatLabel,
  NEWS_FORMAT,
  type OrderableFormat,
  WEEKLY_DIGEST_FORMAT,
} from './formatLabel.ts';
import styles from './GenerationDialog.module.css';
import { InfoPopover } from './InfoPopover.tsx';
import { Modal } from './Modal.tsx';
import { parseStyleVersions, pickableStyles } from './styleVersions.ts';

const STATE_LABEL: Readonly<Record<GenerationRequestState, string>> = {
  queued: 'в очереди',
  taken: 'в работе',
  done: 'готово',
  failed: 'не вышло',
  cancelled: 'отменена',
};

/** Пилюли из base.css — свои цвета не заводим (docs/ui-design.md §2.1). */
const STATE_PILL: Readonly<Record<GenerationRequestState, string>> = {
  queued: 'st-wait',
  taken: 'st-sched',
  done: 'st-done',
  failed: 'st-err',
  cancelled: 'st-hold',
};

/** Ширина модалки: обычная и режим контекста, где поле занимает пол-экрана. */
const WIDTH_NORMAL = 560;
const WIDTH_CONTEXT = 860;

/**
 * С какой длины подсказки виден счётчик — с 9 000 при потолке 10 000 (docs/release-2026-09-19.md
 * §7). Постоянный «12 из 10000» ничего не сообщает; счётчик нужен там, где до потолка осталось
 * меньше абзаца-другого выдержки, которую сюда кладут.
 */
const PROMPT_COUNTER_FROM = GENERATION_PROMPT_MAX - 1000;

/**
 * Три формы контекста — они же три пути, которые писатель различает сам (§6.2). Стоят
 * плейсхолдером, а не подписью над полем: это ПРИМЕРЫ начала фразы, и человек продолжает
 * писать с той же строки, а не сверяется со списком выше.
 */
const CONTEXT_PLACEHOLDER = [
  'Опубликуй следующий текст: …',
  'Напиши новость по этой ссылке: …',
  'Что должно получиться: …',
].join('\n');

/**
 * Контекст обзора конференции — анонсы, а не готовый пост: обзор писатель собирает сам. Тоже
 * пример начала фразы, а не подпись над полем.
 */
const EVENT_CONTEXT_PLACEHOLDER = [
  'Анонсы конференции: …',
  'Что показали — с цифрами, ценами и датами: …',
].join('\n');

/**
 * Первая строка контекста — подпись заявки в списке. Целиком контекст сюда класть нельзя:
 * это бывает пост на две тысячи знаков, и строка списка превратилась бы в стену, срезанную
 * многоточием посередине слова.
 */
function firstLine(text: string): string {
  const lines = text.split('\n');
  const idx = lines.findIndex((l) => l.trim() !== '');
  if (idx < 0) return text.trim();
  const head = (lines[idx] ?? '').trim();
  // Многоточие означает «дальше есть текст», а не «строку обрезали»: обрезку по ширине
  // делает CSS, и второе многоточие рядом с ним читалось бы как часть текста.
  const more = lines.slice(idx + 1).some((l) => l.trim() !== '');
  return more ? `${head} …` : head;
}

/** Чем заявка подписана в списке: тем, что человек в неё вложил, — в порядке убывания смысла. */
function describe(r: GenerationRequest): string {
  if (r.context !== null) return firstLine(r.context);
  if (r.prompt !== null) return r.prompt;
  if (r.source_url !== null) return r.source_url;
  if (r.format_hint !== null) return formatLabel(r.format_hint);
  return 'без подсказки';
}

/**
 * Стиль заявки в мете строки: «сравнение v5 / v6» у пары, «стиль v6» у явного выбора, пусто
 * у умолчания. Выбора нет (`null`) — это «по умолчанию на момент прогона», и называть его
 * именем сегодняшнего умолчания значило бы угадывать.
 */
function styleMeta(r: GenerationRequest): string {
  if (r.compare_style_version_id !== null) {
    return ` · сравнение ${r.style_name ?? 'по умолчанию'} / ${r.compare_style_name ?? '—'}`;
  }
  return r.style_name === null ? '' : ` · стиль ${r.style_name}`;
}

/**
 * Куда ведёт заявка из списка: готовая пара сравнения — на экран «бок о бок» (там названа и
 * половина, которая не вышла, с причиной), обычная — в свой черновик.
 */
function requestHref(r: GenerationRequest): string | null {
  const brand = encodeURIComponent(r.brand);
  if (r.state === 'done' && r.compare_style_version_id !== null) {
    return `/b/${brand}/compare/${encodeURIComponent(r.request_id)}`;
  }
  return r.draft_id === null ? null : `/b/${brand}/draft/${encodeURIComponent(r.draft_id)}`;
}

/**
 * Адрес со схемой в свободном тексте. Копия `BARE_URL_G` из `contracts/telegram/draft.ts`, и
 * копия вынужденная: там она приватная, а экспортированный `checkLinkPolicy` принимает
 * `DraftContent` — текст плюс сущности плюс настройки карточки, — которого у поля ввода нет
 * и быть не может. Расходиться им нечем: обе ищут схему, а не «похожее на адрес».
 *
 * Голый домен без схемы (`geoboost.ru/blog`) здесь НЕ ищется намеренно. На приёме версии его
 * ловит `expectedServerEntities` — детектор сервера Telegram, — а воспроизвести детектор в
 * поле ввода значит либо тянуть в диалог `@smm/contracts/preview`, либо угадывать его
 * правила; угаданный детектор красил бы «т.е.» и «и.о.» как адреса.
 */
const URL_WITH_SCHEME_G = /(?<![A-Za-z0-9+.-])([A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s)\]>»"']{3,})/g;

/**
 * БЕЛЫЙ СПИСОК АДРЕСОВ БРЕНДА НА ПРОВОД ПОКА НЕ ВЫВЕДЕН, и это надо сказать прямо, а не
 * спрятать за пустым массивом. `links.allowed_hosts` живёт в конфиге бренда
 * (`config/brands/<brand>.yaml`), собирает список только валидатор на приёме
 * (`domain/intake.ts::enforceLinkPolicy`), и ни один маршрут, который читает UI, его не
 * отдаёт: ни `GET /api/v1/brands` (`BrandSchema` — шесть полей, хостов среди них нет), ни
 * ответ очереди заявок.
 *
 * Поэтому проверка здесь идёт ТЕМ ЖЕ `isHostAllowed`, что и на приёме, но с ПУСТЫМ списком —
 * то есть говорит ровно то, что интерфейс знает: «вот адреса, каждый из них обязан быть в
 * белом списке». Как только список поедет ответом (одно поле рядом с `known_formats`),
 * правка сводится к подстановке его сюда, и предупреждение станет вердиктом.
 */
const KNOWN_ALLOWED_HOSTS: readonly string[] = [];

export interface ContextLinks {
  /** Хосты, про которые интерфейс не может утверждать, что они разрешены. */
  readonly unknown: readonly string[];
  /** Адреса не по https: `TelegramUrl` не принимает их вовсе, до политики дело не дойдёт. */
  readonly nonHttps: readonly string[];
}

/**
 * ИЗВЕСТНАЯ ГРАНИЦА, НАЗВАННАЯ ЧЕЛОВЕКУ (§6.4). Вставленный пост со ссылками на произвольные
 * домены отклонит ПРАВИЛО C3-1 на приёме версии — то есть УЖЕ ПОСЛЕ платного вызова модели:
 * заявка закроется `failed`, черновика не будет, деньги за прогон потрачены. Узнавать об
 * этом из однострочного отказа через минуту — ровно то, что просили убрать.
 */
export function contextLinks(text: string, allowedHosts: readonly string[]): ContextLinks {
  const unknown: string[] = [];
  const nonHttps: string[] = [];
  for (const m of text.matchAll(URL_WITH_SCHEME_G)) {
    const url = m[1];
    if (url === undefined) continue;
    // Пустой хост от `linkHostOf` означает «это не https-адрес» — так он и задуман.
    const host = linkHostOf(url);
    if (host === '') {
      if (!nonHttps.includes(url)) nonHttps.push(url);
      continue;
    }
    if (isHostAllowed(host, allowedHosts)) continue;
    if (!unknown.includes(host)) unknown.push(host);
  }
  return { unknown, nonHttps };
}

export function GenerationDialog({
  brand,
  onClose,
  onSubmitted,
  initialStyleId = null,
}: {
  brand: string;
  onClose: () => void;
  /** Заявка принята сервером. Шапке она нужна, чтобы показать ход прогона немедленно. */
  onSubmitted?: ((request: GenerationRequest) => void) | undefined;
  /**
   * Стиль, с которым диалог открывается: «Проверить в генерации» редактора стилей открывает
   * его на той версии, которую человек только что правил. `null` — умолчание бренда.
   */
  initialStyleId?: string | null;
}): JSX.Element {
  const promptId = useId();
  const contextId = useId();
  const urlId = useId();
  const eventId = useId();

  const list = useResource<GenerationRequestsResponse>(
    `/api/v1/generation-requests?brand=${encodeURIComponent(brand)}&limit=6`,
  );
  const styleRes = useResource<unknown>(`/api/v1/styles?brand=${encodeURIComponent(brand)}`);

  // Чипы формата — список бренда (шапка файла); первым в нём стоит умолчание.
  const formats = brandFormats(useBrands(), brand);
  /** Выбор человека; `null` — умолчание бренда (первый чип). */
  const [formatChoice, setFormatChoice] = useState<OrderableFormat | null>(null);
  const [prompt, setPrompt] = useState('');
  /** Название конференции для обзора — своё поле, не подсказка новости (шапка файла). */
  const [eventName, setEventName] = useState('');
  const [context, setContext] = useState('');
  /** Режим, а не формат: подсказка формата остаётся ровно той, что была выбрана. */
  const [contextMode, setContextMode] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Что именно приняли: у прогона по контексту, у дайджеста и у обзора конференции другое
   * обещание сроков, у сравнения — два поста.
   */
  const [accepted, setAccepted] = useState<{
    readonly context: boolean;
    readonly compare: boolean;
    readonly digest: boolean;
    readonly eventReview: boolean;
  } | null>(null);
  /** Выбор человека; `null` — умолчание бренда. */
  const [styleChoice, setStyleChoice] = useState<string | null>(initialStyleId);
  const [compareOn, setCompareOn] = useState(false);
  const [compareChoice, setCompareChoice] = useState<string | null>(null);

  // Нажатый формат выводится, а не хранится готовым: выбор, которого нет в списке бренда (список
  // приехал после открытия), молча не уходит — нажатым становится умолчание.
  const format: OrderableFormat =
    formatChoice !== null && formats.includes(formatChoice)
      ? formatChoice
      : (formats[0] ?? NEWS_FORMAT);
  const digest = format === WEEKLY_DIGEST_FORMAT;
  const eventReview = format === EVENT_REVIEW_FORMAT;
  // Дайджест не берёт ни ссылку, ни контекст, обзор конференции — ссылку (шапка файла):
  // набранное остаётся в состоянии — вернувшись к новости, человек найдёт его на месте, — но
  // не уходит и не проверяется.
  const typedUrl = digest || eventReview ? '' : url.trim();
  const inContext = contextMode && !digest;
  const typedContext = context.trim();
  // Подсказка новости и название конференции — разные поля; в режиме контекста не уходит ни то,
  // ни другое: контекст и есть указание писателю.
  const typedPrompt = inContext ? '' : eventReview ? eventName.trim() : prompt.trim();

  // Проверка повторяет регулярку из схемы тела: не отправляем то, что бэкенд отобьёт, и
  // говорим почему сразу, а не после нажатия.
  const urlIssue =
    typedUrl === '' || GENERATION_SOURCE_URL_RE.test(typedUrl)
      ? null
      : 'Ссылка должна начинаться с http:// или https://';

  const links = inContext ? contextLinks(context, KNOWN_ALLOWED_HOSTS) : null;

  // ── стиль ──
  const styleList = parseStyleVersions(styleRes.data);
  const stylesBroken =
    styleRes.error !== undefined || (styleRes.data !== undefined && styleList === null);
  const defaultStyleId = styleList?.default_id ?? null;
  const pickable = styleList === null ? [] : pickableStyles(styleList.versions, null);
  // Пока список в пути, выбор человека (или стиль, с которым диалог открыли) уходит как есть —
  // проверит бэкенд. Когда список пришёл, версия вне него (скрыта, не этого бренда) молча не
  // уходит: выбор сбрасывается на умолчание, и нажатый чип умолчания это показывает.
  const chosen =
    styleList === null || styleChoice === null || pickable.some((v) => v.id === styleChoice)
      ? styleChoice
      : null;
  const firstStyleId = chosen ?? defaultStyleId;
  const compareCandidates = pickable.filter((v) => v.id !== firstStyleId);
  // Второй стиль выводится, а не хранится готовым: смена первого на тот, что стоял вторым,
  // иначе дала бы сравнение стиля с самим собой. Не выбран или стал недопустим — первый из
  // оставшихся: раскрытый ряд без нажатого чипа запускал бы обычный прогон под видом сравнения.
  const compareId = !compareOn
    ? null
    : compareChoice !== null && compareCandidates.some((v) => v.id === compareChoice)
      ? compareChoice
      : (compareCandidates[0]?.id ?? null);
  const canCompare = styleList !== null && compareCandidates.length > 0;
  const styleLabel = (id: string, name: string): string =>
    id === defaultStyleId ? `${name} (по умолчанию)` : name;
  // В режиме контекста пустое поле — не «заявка без подсказки», а заявка не того вида: без
  // текста прогон по контексту молча превратился бы в обычный, с обходом источников.
  const blocked = busy || urlIssue !== null || (inContext && typedContext === '');

  const submit = (): void => {
    setBusy(true);
    setError(null);
    setAccepted(null);
    // Оба ключа уходят ВСЕГДА: схема тела объявляет их обязательными и nullable, то есть
    // «поле не прислали» и «поле пусто» — разные вещи, и первое это 400.
    post<GenerationRequest>('/api/v1/generation-requests', {
      brand,
      format_hint: format,
      prompt: typedPrompt === '' ? null : typedPrompt,
      context: inContext ? typedContext : null,
      source_url: typedUrl === '' ? null : typedUrl,
      // Модель на заявке больше не едет: она — настройка бренда и живёт в шапке
      // (`WriterModelSwitch`). Поле контракта осталось для вызовов мимо интерфейса.
      model: null,
      // Умолчание — `null`, а не его сегодняшний id: «по умолчанию» значит «тем, что стоит в
      // момент прогона», ровно как у модели.
      style_version_id: chosen === null || chosen === defaultStyleId ? null : chosen,
      compare_style_version_id: compareId,
    })
      .then((created) => {
        setBusy(false);
        setAccepted({
          context: created.context !== null,
          compare: created.compare_style_version_id !== null,
          digest: created.format_hint === WEEKLY_DIGEST_FORMAT,
          eventReview: created.format_hint === EVENT_REVIEW_FORMAT,
        });
        // Форма чистится: следующая заявка — это следующая заявка, а не правка прошлой.
        // Сравнение — тоже: двойной по цене прогон не повторяется незаметно вторым нажатием.
        setPrompt('');
        setEventName('');
        setContext('');
        setContextMode(false);
        setUrl('');
        setFormatChoice(null);
        setStyleChoice(initialStyleId);
        setCompareOn(false);
        setCompareChoice(null);
        list.refetch();
        onSubmitted?.(created);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiRequestError || e instanceof Error ? e.message : String(e));
      });
  };

  const cancel = (id: string): void => {
    post(`/api/v1/generation-requests/${encodeURIComponent(id)}/cancel`, {})
      .then(() => {
        list.refetch();
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiRequestError || e instanceof Error ? e.message : String(e));
      });
  };

  const requests = list.data?.requests ?? [];

  return (
    <Modal
      title="Запустить генерацию"
      width={inContext ? WIDTH_CONTEXT : WIDTH_NORMAL}
      onClose={onClose}
    >
      {accepted === null ? null : (
        <div className="val val-info" role="status">
          {accepted.context ? (
            <span>
              Заявка принята. Обхода источников не будет — прогон по контексту начинает писать
              сразу, в течение секунд: тема берётся из вашего текста. Ход виден в шапке, слева от
              кнопки. Готовый черновик придёт в бэклог.
              {accepted.compare
                ? ' Черновиков будет два — пара откроется рядом из списка ниже.'
                : ''}
            </span>
          ) : accepted.digest ? (
            <span>
              Заявка принята. Сначала мониторинг соберёт новости за последние 5 дней и отберёт для
              дайджеста от 3 до 5 поводов — это дольше обычного обхода, до нескольких минут. Затем
              сервис генерации напишет черновик — ход виден в шапке, слева от кнопки. Готовый
              черновик придёт в бэклог.
              {accepted.compare
                ? ' Черновиков будет два — пара откроется рядом из списка ниже.'
                : ''}
            </span>
          ) : accepted.eventReview ? (
            <span>
              Заявка принята. Сначала мониторинг найдёт материалы конференции из списка наблюдения
              бренда за последние 7 дней и проверит, что она прошла; затем сервис генерации напишет
              обзор — ход виден в шапке, слева от кнопки. Готовый черновик придёт в бэклог. Не
              найдётся прошедшей конференции — заявка закроется «не вышло» с причиной, черновика не
              будет.
              {accepted.compare
                ? ' Черновиков будет два — пара откроется рядом из списка ниже.'
                : ''}
            </span>
          ) : (
            <span>
              Заявка принята. Сначала мониторинг обойдёт источники и подберёт тему (обычно 1–3
              минуты), затем сервис генерации напишет черновик — ход виден в шапке, слева от кнопки.
              Готовый черновик придёт в бэклог.
              {accepted.compare
                ? ' Черновиков будет два — пара откроется рядом из списка ниже.'
                : ''}
            </span>
          )}
        </div>
      )}

      {/* fieldset + legend, а не div с role="group": группа переключателей — ровно тот
          случай, ради которого элемент существует, и подпись связывается с ней сама. Чипы —
          форматы бренда, и один из них нажат всегда: «формат не выбран» больше не бывает. ⓘ
          стоит у формата, только когда его чип есть: объяснение кнопки, которой нет, — шум. */}
      <fieldset className={styles['field']}>
        <legend className="lbl">Формат</legend>
        <div className={styles['chips']}>
          {formats.map((f) => (
            <button
              key={f}
              type="button"
              className={format === f ? 'fchip on' : 'fchip'}
              aria-pressed={format === f}
              onClick={() => {
                setFormatChoice(f);
              }}
            >
              {formatLabel(f)}
            </button>
          ))}
          {formats.includes(WEEKLY_DIGEST_FORMAT) ? (
            <InfoPopover label="Что такое дайджест недели">
              <b>Три–пять главных новостей рынка за последние 5 дней, с номерами</b>
              <span>
                Поводы собирает мониторинг сам, каждый — со ссылкой на свой источник: общие для
                маркетологов события, хотя бы одно с рынка России и одно с международного, если
                такие были за неделю (нет — это сказано в обосновании темы). Ссылка и контекст для
                дайджеста не нужны.
              </span>
              <span>
                По расписанию бренда дайджест заказывается и сам — такой черновик придёт в бэклог с
                пометкой «авто».
              </span>
            </InfoPopover>
          ) : null}
          {formats.includes(EVENT_REVIEW_FORMAT) ? (
            <InfoPopover label="Что такое обзор конференции">
              <b>Главное с прошедшей конференции и что это меняет</b>
              <span>
                Не список всего, что показали, а несколько главных анонсов и их следствия для
                бизнеса и экономики — одним связным постом.
              </span>
              <span>
                Материалы ищет мониторинг в источниках списка наблюдения бренда за последние 7 дней
                и сам проверяет, что конференция прошла. Поле «Конференция» выбирает из списка по
                названию или ключевым словам; пустое — любую, что прошла. Ссылка не нужна.
              </span>
              <span>
                «Задать контекст» — свои анонсы вместо обхода источников: писатель начнёт сразу, но
                ссылки на первоисточник в посте не будет.
              </span>
              <span>
                У конференции один обзор в год: пока он жив, второй заказ сервер отклонит.
              </span>
            </InfoPopover>
          ) : null}
        </div>
      </fieldset>

      {/* Чип режима стоит рядом с чипами форматов, но СНАРУЖИ их группы: внутри fieldset он
          попал бы под подпись «Формат» и читался бы как третий формат. Режим и формат
          независимы — в режиме контекста выбранная новость остаётся в силе. У дайджеста
          режима нет: контекст обошёл бы мониторинг, который этот формат и собирает. */}
      <div className={styles['modeRow']}>
        <button
          type="button"
          className={inContext ? 'fchip on' : 'fchip'}
          aria-pressed={inContext}
          disabled={digest}
          title={digest ? 'Дайджест собирается из новостей — контекст ему не нужен' : undefined}
          onClick={() => {
            setContextMode((v) => !v);
          }}
        >
          Задать контекст
        </button>
        <InfoPopover label="Что такое контекст">
          <span>
            Свой готовый пост, ссылка или описание — целиком, вместо короткой подсказки. Тема
            берётся из вашего текста.
          </span>
          <span>
            {`Режим, а не формат: пост выйдет ${eventReview ? 'обзором конференции' : 'новостью'}, а обход источников мониторингом пропускается.`}
          </span>
        </InfoPopover>
      </div>

      {/* Стиль — после формата и режима: это «чем написать», а не «что и откуда». Ряд
          появляется, когда список версий пришёл; отказ списка назван, и прогон при этом
          пойдёт стилем по умолчанию — ровно тем, что и уйдёт на сервер. */}
      <fieldset className={styles['field']}>
        <legend className="lbl">Стиль</legend>
        {stylesBroken ? (
          <div className="val val-err">
            <span>Версии стиля не загрузились — прогон пойдёт стилем по умолчанию.</span>
          </div>
        ) : styleList === null ? null : (
          <div className={styles['chips']}>
            {pickable.map((v) => {
              const on = v.id === firstStyleId;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={on ? 'fchip on' : 'fchip'}
                  aria-pressed={on}
                  onClick={() => {
                    setStyleChoice(v.id === defaultStyleId ? null : v.id);
                  }}
                >
                  {styleLabel(v.id, v.name)}
                </button>
              );
            })}
            <button
              type="button"
              className={compareOn ? 'fchip on' : 'fchip'}
              aria-pressed={compareOn}
              disabled={!canCompare}
              title={
                canCompare ? undefined : 'Нужна вторая версия стиля — её делают копией в редакторе'
              }
              onClick={() => {
                setCompareOn((v) => !v);
              }}
            >
              Сравнить с…
            </button>
            <InfoPopover label="Что такое сравнение стилей">
              <b>Два черновика по одной теме — откроются рядом</b>
              <span>
                Писатель берёт тему один раз и пишет её дважды: первый черновик — стилем из ряда
                «Стиль», второй — выбранным в ряду «Сравнить с». Формат и бросок формы у обоих одни,
                поэтому разница между ними — в стиле.
              </span>
              <span>Писатель работает дважды — прогон стоит почти как два поста.</span>
              <span>
                Второй не вышел — заявка всё равно закроется первым черновиком, а причина будет на
                экране сравнения.
              </span>
            </InfoPopover>
          </div>
        )}
      </fieldset>

      {compareOn && canCompare ? (
        <fieldset className={styles['field']}>
          <legend className="lbl">Сравнить с</legend>
          <div className={styles['chips']}>
            {compareCandidates.map((v) => {
              const on = v.id === compareId;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={on ? 'fchip on' : 'fchip'}
                  aria-pressed={on}
                  onClick={() => {
                    setCompareChoice(v.id);
                  }}
                >
                  {styleLabel(v.id, v.name)}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {inContext ? (
        <div>
          <label className="lbl" htmlFor={contextId}>
            Контекст — уйдёт писателю целиком
          </label>
          <textarea
            id={contextId}
            className={`inp ${styles['context']}`}
            value={context}
            maxLength={GENERATION_CONTEXT_MAX}
            placeholder={eventReview ? EVENT_CONTEXT_PLACEHOLDER : CONTEXT_PLACEHOLDER}
            onChange={(e) => {
              setContext(e.target.value);
            }}
          />
          {context.length > GENERATION_CONTEXT_MAX - 2000 ? (
            <span className="micro">
              {`${String(context.length)} из ${String(GENERATION_CONTEXT_MAX)} символов`}
            </span>
          ) : null}
          {links !== null && links.nonHttps.length > 0 ? (
            <div className="val val-err">
              <span>
                {`Не по https, такой адрес в пост не попадёт вовсе: ${links.nonHttps.join(', ')}`}
              </span>
            </div>
          ) : null}
          {links !== null && links.unknown.length > 0 ? (
            <div className="val val-warn">
              <span>
                {`Ссылки в тексте: ${links.unknown.join(', ')}. В пост попадут только адреса из белого списка бренда (домены бренда + домен первоисточника темы). Остальные отклонит проверка на приёме — уже после платного вызова модели, черновика при этом не будет.`}
              </span>
            </div>
          ) : null}
        </div>
      ) : eventReview ? (
        // Название конференции — необязательный фильтр списка наблюдения бренда (шапка файла).
        <div>
          <label className="lbl" htmlFor={eventId}>
            Конференция
          </label>
          <input
            id={eventId}
            className="inp"
            value={eventName}
            maxLength={GENERATION_PROMPT_MAX}
            placeholder="например, Apple WWDC"
            onChange={(e) => {
              setEventName(e.target.value);
            }}
          />
        </div>
      ) : (
        <div>
          <label className="lbl" htmlFor={promptId}>
            Подсказка
          </label>
          <textarea
            id={promptId}
            className={`inp ${styles['area']}`}
            value={prompt}
            rows={4}
            maxLength={GENERATION_PROMPT_MAX}
            placeholder="что искать, на что опереться, чего избегать"
            onChange={(e) => {
              setPrompt(e.target.value);
            }}
          />
          {/* Счётчик появляется только у границы (§7: с 9 000): постоянный «12 из 10000» ничего
              не сообщает. Это данные, а не объяснение, — поэтому он в теле, цифрами в колонку. */}
          {prompt.length >= PROMPT_COUNTER_FROM ? (
            <span className={`micro ${styles['counter']}`} data-testid="prompt-counter">
              {`${String(prompt.length)} из ${String(GENERATION_PROMPT_MAX)} символов`}
            </span>
          ) : null}
        </div>
      )}

      {/* У дайджеста и обзора конференции поле ссылки не прячется молча, а заменяется одной
          строкой о том, откуда они берутся: пропавшее поле без причины читалось бы как сбой
          вёрстки. */}
      {digest ? (
        <div className="val val-info" data-testid="digest-hint">
          <span>Дайджест собирается из новостей за последние 5 дней — ссылка не нужна.</span>
        </div>
      ) : eventReview ? (
        <div className="val val-info" data-testid="event-review-hint">
          <span>
            {inContext
              ? 'Обзор пишется по вашему тексту — ссылка не нужна, и в посте её не будет.'
              : 'Материалы конференции найдёт мониторинг в источниках бренда — ссылка не нужна.'}
          </span>
        </div>
      ) : (
        <div>
          <label className="lbl" htmlFor={urlId}>
            Ссылка
          </label>
          <input
            id={urlId}
            className="inp"
            value={url}
            maxLength={2000}
            placeholder="https://…"
            onChange={(e) => {
              setUrl(e.target.value);
            }}
          />
          {urlIssue === null ? null : (
            <span className="val val-err" role="alert">
              {urlIssue}
            </span>
          )}
        </div>
      )}

      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Закрыть
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={submit}
        >
          Запустить
        </button>
      </div>

      {requests.length === 0 ? null : (
        <>
          <hr className="hr" />
          <div className={styles['queue']}>
            <span className="lbl">Последние заявки</span>
            {requests.map((r) => {
              const href = requestHref(r);
              return (
                <div key={r.request_id} className={styles['qrow']}>
                  <span className={`pill ${STATE_PILL[r.state]} ${styles['qstate']}`}>
                    {STATE_LABEL[r.state]}
                  </span>
                  <div className={styles['qbody']}>
                    {href === null ? (
                      <div className={styles['qtitle']} title={r.context ?? undefined}>
                        {describe(r)}
                      </div>
                    ) : (
                      <Link
                        href={href}
                        className={styles['qtitle']}
                        title={r.context ?? undefined}
                        onClick={onClose}
                      >
                        {describe(r)}
                      </Link>
                    )}
                    <div className="meta">
                      {`${r.requested_by_login} · ${shortMsk(r.created_at)}`}
                      {r.context === null ? '' : ' · контекст'}
                      {r.format_hint === null ? '' : ` · ${formatLabel(r.format_hint)}`}
                      {styleMeta(r)}
                    </div>
                  </div>
                  {r.state === 'queued' ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-label="Отменить заявку"
                      onClick={() => {
                        cancel(r.request_id);
                      }}
                    >
                      Отменить
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
